/**
 * SearchSettingsEditor 组件
 *
 * 搜索供应商配置管理的主容器，采用两栏布局：
 * - 左侧：供应商选择器
 * - 右侧：供应商配置 + Multi-Key 管理
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { clsx } from 'clsx';
import {
  searchAdminApi,
  type SearchProviderInfo,
  type SearchProviderCreate,
} from '../../../services/searchAdminApi';
import { SearchProviderSelector, type SearchProviderOption } from './SearchProviderSelector';
import { SearchApiKeyManager } from './SearchApiKeyManager';
import { StatusBadge } from '../shared';

interface SearchSettingsEditorProps {
  onDataChange?: () => void;
}

const SEARCH_TYPE_OPTIONS = [
  { value: 'general', label: '一般' },
  { value: 'novel', label: '小说' },
];

const PROVIDER_TYPE_OPTIONS = [
  { value: 'bailian', label: '阿里云百练' },
  { value: 'tavily', label: 'Tavily' },
  { value: 'qianfan', label: '百度千帆' },
  { value: 'shuqi', label: '书旗小说' },
];

export const SearchSettingsEditor: React.FC<SearchSettingsEditorProps> = ({
  onDataChange,
}) => {
  const [providers, setProviders] = useState<SearchProviderInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [showAddProviderForm, setShowAddProviderForm] = useState(false);
  const [providerConfigCollapsed, setProviderConfigCollapsed] = useState(false);

  const [newProviderForm, setNewProviderForm] = useState<SearchProviderCreate>({
    provider_name: '',
    display_name: '',
    provider_type: 'bailian',
    base_url: '',
    priority: 0,
    weight: 10,
    search_types: ['general'],
    key_selection_mode: 'polling',
    timeout: 30,
  });

  const fetchProviders = useCallback(async () => {
    setLoading(true);
    try {
      const result = await searchAdminApi.listProviders();
      setProviders(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取供应商列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  const providerOptions: SearchProviderOption[] = useMemo(() => {
    return providers.map(p => ({
      provider_name: p.provider_name,
      display_name: p.display_name,
      provider_type: p.provider_type,
      enabled: p.enabled,
      priority: p.priority,
      weight: p.weight,
      search_types: p.search_types,
      key_count: p.key_count,
      enabled_key_count: p.enabled_key_count,
    }));
  }, [providers]);

  const currentProvider = useMemo(() => {
    return providers.find(p => p.provider_name === selectedProvider);
  }, [providers, selectedProvider]);

  const handleAddProvider = useCallback(() => {
    setShowAddProviderForm(true);
  }, []);

  const handleCreateProvider = useCallback(async () => {
    if (!newProviderForm.provider_name.trim()) {
      setError('供应商名称不能为空');
      return;
    }
    setSaving(true);
    try {
      await searchAdminApi.createProvider(undefined, newProviderForm);
      setShowAddProviderForm(false);
      setNewProviderForm({
        provider_name: '',
        display_name: '',
        provider_type: 'bailian',
        base_url: '',
        priority: 0,
        weight: 10,
        search_types: ['general'],
        key_selection_mode: 'polling',
        timeout: 30,
      });
      await fetchProviders();
      onDataChange?.();
      setSuccess('供应商创建成功');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : '创建供应商失败');
    } finally {
      setSaving(false);
    }
  }, [newProviderForm, fetchProviders, onDataChange]);

  const handleDeleteProvider = useCallback(async (name: string) => {
    try {
      await searchAdminApi.deleteProvider(undefined, name);
      if (selectedProvider === name) {
        setSelectedProvider(null);
      }
      await fetchProviders();
      onDataChange?.();
      setSuccess('供应商删除成功');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : '删除供应商失败');
    }
  }, [selectedProvider, fetchProviders, onDataChange]);

  const handleToggleProvider = useCallback(async (name: string, enabled: boolean) => {
    try {
      await searchAdminApi.updateProvider(undefined, name, { enabled });
      await fetchProviders();
      onDataChange?.();
      setSuccess(enabled ? '供应商已启用' : '供应商已禁用');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新供应商失败');
    }
  }, [fetchProviders, onDataChange]);

  const handleUpdateProviderConfig = useCallback(async (updates: Record<string, unknown>) => {
    if (!selectedProvider) return;
    setSaving(true);
    try {
      await searchAdminApi.updateProvider(undefined, selectedProvider, updates);
      await fetchProviders();
      onDataChange?.();
      setSuccess('供应商配置已更新');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新供应商配置失败');
    } finally {
      setSaving(false);
    }
  }, [selectedProvider, fetchProviders, onDataChange]);

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4" />
        加载中...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium text-gray-800">供应商配置</h2>
        <button
          type="button"
          onClick={fetchProviders}
          disabled={loading}
          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
        >
          刷新
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
          <button onClick={() => setError('')} className="ml-4 text-red-500 hover:text-red-700">关闭</button>
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">{success}</div>
      )}

      {/* Add Provider Form */}
      {showAddProviderForm && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
          <h3 className="font-medium text-gray-800">添加新供应商</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">供应商名称 *</label>
              <input
                value={newProviderForm.provider_name}
                onChange={(e) => setNewProviderForm({ ...newProviderForm, provider_name: e.target.value })}
                placeholder="例: bailian"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">显示名称</label>
              <input
                value={newProviderForm.display_name}
                onChange={(e) => setNewProviderForm({ ...newProviderForm, display_name: e.target.value })}
                placeholder="例: 阿里云百练"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">供应商类型 *</label>
              <select
                value={newProviderForm.provider_type}
                onChange={(e) => setNewProviderForm({ ...newProviderForm, provider_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              >
                {PROVIDER_TYPE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">基础 URL</label>
              <input
                value={newProviderForm.base_url}
                onChange={(e) => setNewProviderForm({ ...newProviderForm, base_url: e.target.value })}
                placeholder="例: https://api.example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">优先级</label>
              <input
                type="number"
                value={newProviderForm.priority}
                onChange={(e) => setNewProviderForm({ ...newProviderForm, priority: parseInt(e.target.value, 10) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">权重</label>
              <input
                type="number"
                value={newProviderForm.weight}
                onChange={(e) => setNewProviderForm({ ...newProviderForm, weight: parseInt(e.target.value, 10) || 10 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">搜索类型</label>
            <div className="flex gap-4">
              {SEARCH_TYPE_OPTIONS.map(opt => (
                <label key={opt.value} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={(newProviderForm.search_types || []).includes(opt.value)}
                    onChange={(e) => {
                      const currentTypes = newProviderForm.search_types || [];
                      const types = e.target.checked
                        ? [...currentTypes, opt.value]
                        : currentTypes.filter(t => t !== opt.value);
                      setNewProviderForm({ ...newProviderForm, search_types: types });
                    }}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 pt-2">
            <button
              onClick={handleCreateProvider}
              disabled={saving}
              className="px-4 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 disabled:opacity-50"
            >
              {saving ? '创建中...' : '创建'}
            </button>
            <button
              onClick={() => setShowAddProviderForm(false)}
              className="px-4 py-2 bg-gray-100 text-gray-600 text-sm rounded-lg hover:bg-gray-200"
            >
              取消
            </button>
          </div>
        </div>
      )}

      {/* Two-Column Layout */}
      <div className="flex gap-6" style={{ height: 'calc(100vh - 300px)', minHeight: '400px' }}>
        {/* Left Column - Provider Selector */}
        <div className="w-80 flex-shrink-0 overflow-y-auto pr-2">
          <SearchProviderSelector
            providers={providerOptions}
            selectedProvider={selectedProvider}
            onSelect={setSelectedProvider}
            onAdd={handleAddProvider}
            onDelete={handleDeleteProvider}
            disabled={saving}
          />
        </div>

        {/* Right Column - Provider Config + Key Manager */}
        <div className="flex-1 overflow-y-auto min-w-0">
          {!selectedProvider ? (
            <div className="h-full flex items-center justify-center p-12 border-2 border-dashed border-gray-200 rounded-lg">
              <div className="text-center text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <p>请从左侧选择一个供应商开始配置</p>
              </div>
            </div>
          ) : !currentProvider ? (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700">
              供应商不存在或已被删除
            </div>
          ) : (
            <div className="space-y-6 pb-6">
              {/* Provider Config Card */}
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-800">供应商配置</h3>
                  <div className="flex items-center gap-2">
                    <StatusBadge variant={currentProvider.enabled ? 'success' : 'neutral'} size="sm">
                      {currentProvider.enabled ? '启用' : '禁用'}
                    </StatusBadge>
                    <StatusBadge variant="info" size="sm">
                      {PROVIDER_TYPE_OPTIONS.find(o => o.value === currentProvider.provider_type)?.label || currentProvider.provider_type}
                    </StatusBadge>
                    <button
                      type="button"
                      onClick={() => setProviderConfigCollapsed(!providerConfigCollapsed)}
                      className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100 transition-colors"
                      title={providerConfigCollapsed ? '展开' : '折叠'}
                    >
                      <svg className={clsx('w-5 h-5 transition-transform', providerConfigCollapsed ? '' : 'rotate-180')}
                           fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className={clsx('space-y-4', providerConfigCollapsed && 'hidden')}>
                  {/* Display Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">显示名称</label>
                    <input
                      type="text"
                      value={currentProvider.display_name}
                      onChange={(e) => handleUpdateProviderConfig({ display_name: e.target.value })}
                      disabled={saving}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                    />
                  </div>

                  {/* Priority & Weight */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">优先级</label>
                      <input
                        type="number"
                        value={currentProvider.priority}
                        onChange={(e) => handleUpdateProviderConfig({ priority: parseInt(e.target.value, 10) || 0 })}
                        disabled={saving}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                      />
                      <p className="mt-1 text-xs text-gray-500">数值越大优先级越高</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">权重</label>
                      <input
                        type="number"
                        value={currentProvider.weight}
                        onChange={(e) => handleUpdateProviderConfig({ weight: parseInt(e.target.value, 10) || 10 })}
                        disabled={saving}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                      />
                      <p className="mt-1 text-xs text-gray-500">加权随机路由的权重</p>
                    </div>
                  </div>

                  {/* Search Types */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">搜索类型</label>
                    <div className="flex gap-4">
                      {SEARCH_TYPE_OPTIONS.map(opt => (
                        <label key={opt.value} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={currentProvider.search_types.includes(opt.value)}
                            onChange={(e) => {
                              const types = e.target.checked
                                ? [...currentProvider.search_types, opt.value]
                                : currentProvider.search_types.filter(t => t !== opt.value);
                              handleUpdateProviderConfig({ search_types: types });
                            }}
                            disabled={saving}
                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">选择该供应商支持的搜索类型</p>
                  </div>

                  {/* Enable/Disable Toggle */}
                  <div className="pt-4 border-t border-gray-200 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleToggleProvider(selectedProvider, !currentProvider.enabled)}
                      disabled={saving}
                      className={clsx(
                        'px-4 py-2 text-sm rounded-lg',
                        currentProvider.enabled
                          ? 'bg-yellow-50 text-yellow-600 hover:bg-yellow-100 border border-yellow-200'
                          : 'bg-green-50 text-green-600 hover:bg-green-100 border border-green-200',
                        saving && 'opacity-50 cursor-not-allowed'
                      )}
                    >
                      {currentProvider.enabled ? '禁用供应商' : '启用供应商'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Multi-Key Manager */}
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <SearchApiKeyManager
                  providerName={selectedProvider}
                  disabled={saving}
                  onRefresh={fetchProviders}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Help Text */}
      <div className="p-4 bg-gray-50 rounded-lg text-xs text-gray-600">
        <strong>说明：</strong>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>供应商：搜索服务提供商，如百练、Tavily、千帆、书旗</li>
          <li>优先级：数值越大优先级越高，同一优先级内按权重随机</li>
          <li>权重：加权随机路由的权重，默认 10</li>
          <li>搜索类型：一般搜索用于通用查询，小说搜索用于小说内容检索</li>
          <li>Multi-Key：支持同一供应商配置多个 API 密钥，密钥按轮询/随机策略选择</li>
        </ul>
      </div>
    </div>
  );
};

export default SearchSettingsEditor;